/**
 * System permission aliases — single source of truth for permission checks.
 *
 * Values are the backend permission `alias` strings consumed by
 * `usePermissions` and `RequireRoutePermit`. Prefer referencing these
 * members over raw string literals so checks stay type-safe and discoverable.
 *
 * @example
 * const [canView] = usePermissions({ allowedPermissions: [Permission.CanViewEmployeeDetails] })
 */
export enum Permission {
  CanViewCompanyDetails = "can_view_company_details",
  CanEditCompanyInfo = "can_edit_company_info",
  CanManageWorkplaces = "can_manage_workplaces",
  CanCreateNewBusinesses = "can_create_new_businesses",
  CanCreateSignatories = "can_create_signatories",
  CanDeleteSignatories = "can_delete_signatories",
  CanViewEmployeeDetails = "can_view_employee_details",
  CanCreateEmployees = "can_create_employees",
  CanApproveEmployees = "can_approve_employees",
  CanUpdateEmployees = "can_update_employees",
  CanDeleteEmployees = "can_delete_employees",
  CanViewOffers = "can_view_offers",
  CanDeleteOffers = "can_delete_offers",
  CanViewOrgChart = "can_view_org_chart",
  CanViewDepartments = "can_view_departments",
  CanCreateDepartments = "can_create_departments",
  CanManageDepartments = "can_manage_departments",
  CanDeleteDepartments = "can_delete_departments",
  CanViewLeaveRequests = "can_view_leave_requests",
  CanCreateLeaveRequests = "can_create_leave_requests",
  CanEditLeaveRequests = "can_edit_leave_requests",
  CanDeleteLeaveRequests = "can_delete_leave_requests",
  CanViewLeavePolicies = "can_view_leave_policies",
  CanCreateLeavePolicies = "can_create_leave_policies",
  CanEditLeavePolicies = "can_edit_leave_policies",
  CanDeleteLeavePolicies = "can_delete_leave_policies",
  CanViewLoanRequests = "can_view_loan_requests",
  CanCreateLoanRequests = "can_create_loan_requests",
  CanUpdateLoanRequests = "can_update_loan_requests",
  CanDeleteLoanRequests = "can_delete_loan_requests",
  CanViewLoanPolicies = "can_view_loan_policies",
  CanCreateLoanPolicies = "can_create_loan_policies",
  CanUpdateLoanPolicies = "can_update_loan_policies",
  CanDeleteLoanPolicies = "can_delete_loan_policies",
  CanViewSalaryAdvanceRequests = "can_view_salary_advance_requests",
  CanCreateSalaryAdvanceRequests = "can_create_salary_advance_requests",
  CanUpdateSalaryAdvanceRequests = "can_update_salary_advance_requests",
  CanDeleteSalaryAdvanceRequests = "can_delete_salary_advance_requests",
  CanViewSalaryAdvancePolicies = "can_view_salary_advance_policies",
  CanCreateSalaryAdvancePolicies = "can_create_salary_advance_policies",
  CanUpdateSalaryAdvancePolicies = "can_update_salary_advance_policies",
  CanDeleteSalaryAdvancePolicies = "can_delete_salary_advance_policies",
  CanViewDocumentRequests = "can_view_document_requests",
  CanCreateDocumentRequests = "can_create_document_requests",
  CanUpdateDocumentRequests = "can_update_document_requests",
  CanDeleteDocumentRequests = "can_delete_document_requests",
  CanViewDocumentPolicies = "can_view_document_policies",
  CanCreateDocumentPolicies = "can_create_document_policies",
  CanUpdateDocumentPolicies = "can_update_document_policies",
  CanDeleteDocumentPolicies = "can_delete_document_policies",
  CanViewRedeploymentRequests = "can_view_redeployment_requests",
  CanCreateRedeploymentRequests = "can_create_redeployment_requests",
  CanUpdateRedeploymentRequests = "can_update_redeployment_requests",
  CanDeleteRedeploymentRequests = "can_delete_redeployment_requests",
  CanViewRedeploymentPolices = "can_view_redeployment_polices",
  CanCreateRedeploymentPolices = "can_create_redeployment_polices",
  CanUpdateRedeploymentPolices = "can_update_redeployment_polices",
  CanDeleteRedeploymentPolices = "can_delete_redeployment_polices",
  CanViewExitRequests = "can_view_exit_requests",
  CanCreateExitRequests = "can_create_exit_requests",
  CanUpdateExitRequests = "can_update_exit_requests",
  CanDeleteExitRequests = "can_delete_exit_requests",
  CanViewExitPolicies = "can_view_exit_policies",
  CanCreateExitPolicies = "can_create_exit_policies",
  CanUpdateExitPolicies = "can_update_exit_policies",
  CanDeleteExitPolicies = "can_delete_exit_policies",
  CanViewDocumentsAndOfferTemplates = "can_view_documents_and_offer_templates",
  CanManageDocuments = "can_manage_documents",
  CanRecallDocuments = "can_recall_documents",
  CanDeleteDocuments = "can_delete_documents",
  CanMakeOffers = "can_make_offers",
  CanCreateOfferTemplates = "can_create_offer_templates",
  CanUpdateOfferTemplates = "can_update_offer_templates",
  CanDeleteOfferTemplates = "can_delete_offer_templates",
  CanViewPayrollChanges = "can_view_payroll_changes",
  CanCreatePayrollChanges = "can_create_payroll_changes",
  CanUpdatePayrollChanges = "can_update_payroll_changes",
  CanDeletePayrollChanges = "can_delete_payroll_changes",
  CanViewPayrollRecords = "can_view_payroll_records",
  CanSubmitPayrolls = "can_submit_payrolls",
  CanCancelPayrolls = "can_cancel_payrolls",
  CanViewSkippedEmployees = "can_view_skipped_employees",
  CanSkipUnskipEmployees = "can_skip/unskip_employees",
  CanViewWalletOverview = "can_view_wallet_overview",
  CanViewWalletTransactions = "can_view_wallet_transactions",
  CanViewAnalytics = "can_view_analytics",
  CanViewEmployees = "can_view_employees",
  CanStopEmployeeEarnings = "can_stop_employee_earnings",
  CanDisableEmployees = "can_disable_employees",
  CanViewWithdrawals = "can_view_withdrawals",
  CanRetryFailedWithdrawals = "can_retry_failed_withdrawals",
  CanViewPaymentNotes = "can_view_payment_notes",
  CanProcessPaymentNotes = "can_process_payment_notes",
  CanViewPaySchedules = "can_view_pay_schedules",
  CanCreatePaySchedules = "can_create_pay_schedules",
  CanProcessPaySchedules = "can_process_pay_schedules",
  CanDeletePaySchedules = "can_delete_pay_schedules",
  CanViewBenefits = "can_view_benefits",
  CanCreateBenefits = "can_create_benefits",
  CanManageBenefits = "can_manage_benefits",
  CanViewPeriods = "can_view_periods",
  CanCreatePeriods = "can_create_periods",
  CanViewObjectives = "can_view_objectives",
  CanCreateObjectives = "can_create_objectives",
  CanUpdateObjectives = "can_update_objectives",
  CanDeleteObjectives = "can_delete_objectives",
  CanViewGroups = "can_view_groups",
  CanCreateGroups = "can_create_groups",
  CanUpdateGroups = "can_update_groups",
  CanDeleteGroups = "can_delete_groups",
  CanViewReviews = "can_view_reviews",
  CanCreateReviews = "can_create_reviews",
  CanUpdateReviews = "can_update_reviews",
  CanDeleteReviews = "can_delete_reviews",
  CanSetRatingWeights = "can_set_rating_weights",
  CanViewTasks = "can_view_tasks",
  CanCreateTasks = "can_create_tasks",
  CanUpdateTasks = "can_update_tasks",
  CanDeleteTasks = "can_delete_tasks",
  CanViewAbsences = "can_view_absences",
  CanCreateAbsences = "can_create_absences",
  CanUpdateAbsences = "can_update_absences",
  CanDeleteAbsences = "can_delete_absences",
  CanViewViolations = "can_view_violations",
  CanCreateViolations = "can_create_violations",
  CanDeleteViolations = "can_delete_violations",
  CanViewViolationPolicies = "can_view_violation_policies",
  CanCreateViolationPolicies = "can_create_violation_policies",
  CanUpdateViolationPolicies = "can_update_violation_policies",
  CanDeleteViolationPolicies = "can_delete_violation_policies",
  CanViewViolationTypes = "can_view_violation_types",
  CanCreateViolationTypes = "can_create_violation_types",
  CanUpdateViolationTypes = "can_update_violation_types",
  CanDeleteViolationTypes = "can_delete_violation_types",
  CanRemoveTeamMembers = "can_remove_team_members",
  CanChangeTeamMemberRoles = "can_change_team_member_roles",
  CanCreateCustomRoles = "can_create_custom_roles",
  CanEditCustomRoles = "can_edit_custom_roles",
  CanInviteNewTeamMembers = "can_invite_new_team_members",
  CanManageHrPayrollSettings = "can_manage_hr_&_payroll_settings",
  CanManagePlanBillingInformation = "can_manage_plan_&_billing_information",
  CanManageApprovalWorkflowSettings = "can_manage_approval_workflow_settings",
  CanManageCustomizationSettings = "can_manage_customization_settings",
  CanViewAssets = "can_view_assets",
  CanCreateAssets = "can_create_assets",
  CanUpdateAssets = "can_update_assets",
  CanDeleteAssets = "can_delete_assets",
  CanViewAnnouncements = "can_view_announcements",
  CanCreateAnnouncements = "can_create_announcements",
  CanUpdateAnnouncements = "can_update_announcements",
  CanDeleteAnnouncements = "can_delete_announcements",
  CanViewAuditTrails = "can_view_audit_trails",
  PrimaryAdmin = "primary_admin",
  SecondaryAdmin = "secondary_admin",
  CanReviewLeaveRequests = "can_review_leave_requests",
  CanReviewLoanRequests = "can_review_loan_requests",
  CanReviewSalaryAdvanceRequests = "can_review_salary_advance_requests",
  CanReviewDocumentRequests = "can_review_document_requests",
  CanReviewRedeploymentRequests = "can_review_redeployment_requests",
  CanReviewExitRequests = "can_review_exit_requests",
  CanReviewPayrollChanges = "can_review_payroll_changes",
  CanReviewPayrolls = "can_review_payrolls",
  CanReviewAbsences = "can_review_absences",
  CanReviewViolations = "can_review_violations",
  CanManageIntegrations = "can_manage_integrations",
  CanViewReports = "can_view_reports",
  CanDownloadReports = "can_download_reports",
  CanManageTriggers = "can_manage_triggers",
  CanRemoveCustomRoles = "can_remove_custom_roles",
  CanManagePayrollConfigurations = "can_manage_payroll_configurations",
  CanCreateJobs = "can_create_jobs",
  CanUpdateJobs = "can_update_jobs",
  CanDeleteJobs = "can_delete_jobs",
  CanViewJobs = "can_view_jobs",
  CanPostJobs = "can_post_jobs",
  CanApproveJobs = "can_approve_jobs",
  CanViewApplicants = "can_view_applicants",
  CanEditPaySchedules = "can_edit_pay_schedules",
  CanReviewPaySchedules = "can_review_pay_schedules",
  CanDeletePeriods = "can_delete_periods",
  CanViewPayrollOverview = "can_view_payroll_overview",
  CanCreateAudience = "can_create_audience",
  CanUpdateAudience = "can_update_audience",
  CanDeleteAudience = "can_delete_audience",
  CanReviewKeyResults = "can_review_key_results",
  CanViewFeedback = "can_view_feedback",
  CanViewConversations = "can_view_conversations",
  CanDeleteConversations = "can_delete_conversations",
  CanViewSuspensions = "can_view_suspensions",
  CanCreateSuspensions = "can_create_suspensions",
  CanUpdateSuspensions = "can_update_suspensions",
  CanDeleteSuspensions = "can_delete_suspensions",
  CanReviewSuspensions = "can_review_suspensions",
  CanViewPayableTypes = "can_view_payable_types",
  CanCreatePayableTypes = "can_create_payable_types",
  CanUpdatePayableTypes = "can_update_payable_types",
  CanDeletePayableTypes = "can_delete_payable_types",
  CanViewCustomGroups = "can_view_custom_groups",
  CanCreateCustomGroups = "can_create_custom_groups",
  CanUpdateCustomGroups = "can_update_custom_groups",
  CanDeleteCustomGroups = "can_delete_custom_groups",
  CanViewCompensationDetails = "can_view_compensation_details",
  CanViewPersonalDetails = "can_view_personal_details",
  CanViewPayrollReports = "can_view_payroll_reports",
  CanViewEmployeeReports = "can_view_employee_reports",
  CanViewCustomReports = "can_view_custom_reports",
  CanViewPersonalRequests = "can_view_personal_requests",
  CanViewPersonalDocuments = "can_view_personal_documents",
  CanViewPersonalBenefits = "can_view_personal_benefits",
  CanViewPersonalAssets = "can_view_personal_assets",
  CanViewPersonalTasks = "can_view_personal_tasks",
  CanViewDisciplinaryDetails = "can_view_disciplinary_details",
  CanViewPersonalNotes = "can_view_personal_notes",
  CanViewPersonalLogs = "can_view_personal_logs",
  CanViewEwaPolicy = "can_view_ewa_policy",
  CanCreateEwaPolicy = "can_create_ewa_policy",
  CanUpdateEwaPolicy = "can_update_ewa_policy",
  CanViewDeductionTypes = "can_view_deduction_types",
  CanCreateDeductionTypes = "can_create_deduction_types",
  CanUpdateDeductionTypes = "can_update_deduction_types",
  CanDeleteDeductionTypes = "can_delete_deduction_types",
  CanCreateWorkplaces = "can_create_workplaces",
  CanManageDutyLocations = "can_manage_duty_locations",
  CanManageShiftPlans = "can_manage_shift_plans",
  CanManageEmployeeAssignments = "can_manage_employee_assignments",
  CanApproveAndRejectAttendanceEntries = "can_approve_and_reject_attendance_entries",
  CanCreateManualTimeEntries = "can_create_manual_time_entries",
  CanEditManualTimeEntries = "can_edit_manual_time_entries",
  CanUploadTimesheets = "can_upload_timesheets",
  CanConfigureAttendanceSettings = "can_configure_attendance_settings",
  CanViewAttendanceRecords = "can_view_attendance_records",
  CanExportAttendanceData = "can_export_attendance_data",
}

/**
 * Time & Attendance permission aliases.
 *
 * Attendance-module permissions — workplaces, duty locations, shift plans,
 * employee assignments, attendance entries, manual time entries, timesheets,
 * attendance settings and records. Values are the backend `alias` strings
 * consumed by `usePermissions` and `RequireRoutePermit`.
 *
 * @example
 * const [canView] = usePermissions({ allowedPermissions: [TimeAndAttendancePermission.CanViewAttendanceRecords] })
 */
export enum TimeAndAttendancePermission {
  CanCreateWorkplaces = "can_create_workplaces",
  CanManageDutyLocations = "can_manage_duty_locations",
  CanManageShiftPlans = "can_manage_shift_plans",
  CanManageEmployeeAssignments = "can_manage_employee_assignments",
  CanApproveAndRejectAttendanceEntries = "can_approve_and_reject_attendance_entries",
  CanCreateManualTimeEntries = "can_create_manual_time_entries",
  CanEditManualTimeEntries = "can_edit_manual_time_entries",
  CanUploadTimesheets = "can_upload_timesheets",
  CanConfigureAttendanceSettings = "can_configure_attendance_settings",
  CanViewAttendanceRecords = "can_view_attendance_records",
  CanExportAttendanceData = "can_export_attendance_data",
}
